import { DATE_FORMAT, getDate, TIMEZONES, IDateInputFormat } from '@belong/date-utils';
import * as Yup from 'yup';

import * as dateValidation from './date';

type ErrorValidationType<ErrorKey, T extends Record<string, any> = Record<string, any>> = {
  errorKey: ErrorKey;
  message: string;
} & T;

type ValidationFunction<T extends Record<string, any> = { value: string }> = (props: { value: string } & T) => boolean;

interface IIsDateAfter {
  minimumDate?: Date;
  timezone?: TIMEZONES;
}

interface IIsDateAfter {
  format?: IDateInputFormat;
  minimumDate?: Date;
  timezone?: TIMEZONES;
}
/** This checks if the provided value is either equal or after minimumDate, defaulting to today */
export const isDateAfter: ValidationFunction<IIsDateAfter> = ({
  format: inputFormat = DATE_FORMAT.INPUT,
  minimumDate = new Date(),
  value,
  timezone
}): boolean => {
  const unit = inputFormat === DATE_FORMAT.INPUT_MONTH_YEAR ? 'month' : 'day';

  let inputDate = getDate(value, inputFormat).endOf(unit);
  let otherDate = getDate(minimumDate, undefined).endOf(unit);

  if (timezone) {
    inputDate = inputDate.tz(timezone);
    otherDate = otherDate.tz(timezone);
  }

  return inputDate.isSameOrAfter(otherDate, unit);
};

interface IIsDateBefore {
  format?: IDateInputFormat;
  maximumDate?: Date;
  timezone?: TIMEZONES;
}
/** This checks if the provided value is either equal or before maximumDate, defaulting to today */
export const isDateBefore: ValidationFunction<IIsDateBefore> = ({
  format: inputFormat = DATE_FORMAT.INPUT,
  maximumDate = new Date(),
  value,
  timezone
}): boolean => {
  const unit = inputFormat === DATE_FORMAT.INPUT_MONTH_YEAR ? 'month' : 'day';

  let inputDate = getDate(value, inputFormat).startOf(unit);
  let otherDate = getDate(maximumDate, undefined).startOf(unit);

  if (timezone) {
    inputDate = inputDate.tz(timezone);
    otherDate = otherDate.tz(timezone);
  }

  return inputDate.isSameOrBefore(otherDate, unit);
};

interface IIsMinimumAge {
  age: number;
}
export const isMinimumAge: ValidationFunction<IIsMinimumAge> = ({ age, value }): boolean => {
  return getDate(new Date()).diff(getDate(value, DATE_FORMAT.INPUT), 'year') >= age;
};

interface IMaxLength {
  max: number;
}
export const maxLength: ValidationFunction<IMaxLength> = ({ max, value }): boolean => value.length <= max;

interface IMinLength {
  min: number;
}
export const minLength: ValidationFunction<IMinLength> = ({ min, value }): boolean => value.length >= min;

interface IMaxNumber {
  max: number;
}
export const maxNumber: ValidationFunction<IMaxNumber> = ({ max, value }): boolean => Number(value) <= max;

interface IMinNumber {
  min: number;
}
export const minNumber: ValidationFunction<IMinNumber> = ({ min, value }): boolean => Number(value) >= min;

export const required: ValidationFunction = ({ value }): boolean => !!value;

interface IValidDate {
  format?: IDateInputFormat;
}

const validDate: ValidationFunction<IValidDate> = ({ format: inputFormat, value }): boolean =>
  dateValidation.validateFormat(value, inputFormat);

interface IValidFormat {
  pattern: RegExp;
  /**
   * `reverseCheck` is used to ensure the value does NOT match the pattern. For example, if you want to allow anything
   * except a regex pattern
   * A good example of this is the `PO_BOX` regex, when we need to allow anything except this regex
   */
  reverseCheck?: boolean;
}
export const validFormat: ValidationFunction<IValidFormat> = ({ pattern, reverseCheck, value }): boolean => {
  const matched = pattern.test(value);
  return reverseCheck ? !matched : matched;
};

const validateYear: ValidationFunction = ({ value }): boolean => dateValidation.validateYear(value);
const withinSixtyDays: ValidationFunction = ({ value }): boolean => dateValidation.withinSixtyDays(value);
const withinDays: ValidationFunction<IMinNumber & IMaxNumber> = ({ value, min, max }): boolean =>
  dateValidation.withinDays(value, min, max);

export type ErrorValidation =
  | ErrorValidationType<'validFormat', IValidFormat>
  | ErrorValidationType<'isDateAfter', IIsDateAfter>
  | ErrorValidationType<'isDateBefore', IIsDateBefore>
  | ErrorValidationType<'isMinimumAge', IIsMinimumAge>
  | ErrorValidationType<'maxLength', IMaxLength>
  | ErrorValidationType<'minLength', IMinLength>
  | ErrorValidationType<'maxNumber', IMaxNumber>
  | ErrorValidationType<'minNumber', IMinNumber>
  | ErrorValidationType<'required'>
  | ErrorValidationType<'validDate'>
  | ErrorValidationType<'validateYear'>
  | ErrorValidationType<'withinSixtyDays'>
  | ErrorValidationType<'withinDays', IMinNumber & IMaxNumber>;

export const validationFunctions = {
  validFormat,
  isDateAfter,
  isDateBefore,
  isMinimumAge,
  maxLength,
  minLength,
  maxNumber,
  minNumber,
  required,
  validDate,
  validateYear,
  withinSixtyDays,
  withinDays
};

export const getValidationSchema = (errorValidation: ErrorValidation[]): Yup.StringSchema<string> => {
  const validationSchema = Yup.string().trim().default('');
  const isRequired = errorValidation.some(({ errorKey }) => errorKey === 'required');

  errorValidation.forEach(({ errorKey, message, ...validationParams }) => {
    if (validationFunctions[errorKey]) {
      validationSchema.withMutation(schema =>
        schema.test(errorKey, message, (inputValue: any) => {
          // Allow the field to be empty if it's not required
          if (!isRequired && !inputValue) {
            return true;
          }

          // @ts-ignore Until we figure out to map the types correctly...
          return validationFunctions[errorKey]({ value: inputValue, ...validationParams });
        })
      );
    }
  });
  return validationSchema;
};
